import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSchoolProfile, putSchoolBranding, putSchoolProfile } from '@/api/owner';
import { Palette, Save } from 'lucide-react';

const COLORS = ['#0A2540', '#0E7C66', '#7C3AED', '#DC2626', '#EA580C', '#0891B2', '#C9A227'];

export function BrandingPage() {
  const qc = useQueryClient();
  const [primary, setPrimary] = useState('#0A2540');
  const [accent, setAccent] = useState('#0E7C66');
  const [name, setName] = useState('');
  const [motto, setMotto] = useState('');

  const profileQ = useQuery({ queryKey: ['owner-profile'], queryFn: () => getSchoolProfile().then((r: any) => r.data?.data ?? r.data) });
  const profile = profileQ.data as any;

  useEffect(() => {
    if (!profile) return;
    setPrimary(profile.primary_color ?? profile.primaryColor ?? '#0A2540');
    setAccent(profile.accent_color ?? profile.accentColor ?? '#0E7C66');
    setName(profile.name ?? '');
    setMotto(profile.motto ?? '');
  }, [profile]);

  const brandingM = useMutation({
    mutationFn: () => putSchoolBranding({ primary_color: primary, accent_color: accent }),
    onSuccess: (r: any) => { alert(r.data?.message ?? 'Branding saved'); qc.invalidateQueries({ queryKey: ['owner-profile'] }); qc.invalidateQueries({ queryKey: ['auth-me-school'] }); },
    onError: (e: any) => alert(e.response?.data?.message ?? 'Failed'),
  });

  const profileM = useMutation({
    mutationFn: () => putSchoolProfile({ name, motto }),
    onSuccess: (r: any) => { alert(r.data?.message ?? 'Profile saved'); qc.invalidateQueries({ queryKey: ['owner-profile'] }); qc.invalidateQueries({ queryKey: ['auth-me-school'] }); },
    onError: (e: any) => alert(e.response?.data?.message ?? 'Failed'),
  });

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto pb-10 p-6">
      <div>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">REPORTS & SETTINGS</p>
        <h1 className="text-3xl font-extrabold text-slate-900">Branding</h1>
        <p className="text-slate-500 text-sm mt-1">Colours, name and motto — appears on parent screens and result sheets</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: form */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
            <h2 className="font-bold text-slate-900">School identity</h2>
            <div><label className="block text-sm font-semibold text-slate-700 mb-1">School name</label><input value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
            <div><label className="block text-sm font-semibold text-slate-700 mb-1">Motto</label><input value={motto} onChange={(e) => setMotto(e.target.value)} placeholder="Knowledge and Character" className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
            <button onClick={() => profileM.mutate()} disabled={profileM.isPending} className="w-full py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
              {profileM.isPending ? 'Saving…' : 'Save identity'}
            </button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
            <h2 className="font-bold text-slate-900">Colours</h2>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Primary</label>
              <div className="flex flex-wrap gap-2">
                {COLORS.map((c) => (
                  <button key={c} onClick={() => setPrimary(c)} className={`w-10 h-10 rounded-lg border-2 ${primary === c ? 'border-slate-900 ring-2 ring-slate-300' : 'border-transparent'}`} style={{ background: c }} />
                ))}
              </div>
              <input type="color" value={primary} onChange={(e) => setPrimary(e.target.value)} className="mt-2 h-10 w-full rounded cursor-pointer" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Accent</label>
              <div className="flex flex-wrap gap-2">
                {COLORS.map((c) => (
                  <button key={c} onClick={() => setAccent(c)} className={`w-10 h-10 rounded-lg border-2 ${accent === c ? 'border-slate-900 ring-2 ring-slate-300' : 'border-transparent'}`} style={{ background: c }} />
                ))}
              </div>
              <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} className="mt-2 h-10 w-full rounded cursor-pointer" />
            </div>
            <button onClick={() => brandingM.mutate()} disabled={brandingM.isPending} className="w-full py-2.5 bg-green-600 text-white rounded-lg font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2">
              <Save className="w-4 h-4" /> {brandingM.isPending ? 'Saving…' : 'Save colours'}
            </button>
          </div>
        </div>

        {/* Right: live preview */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden sticky top-6">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
            <Palette className="w-4 h-4 text-slate-500" />
            <h2 className="font-bold text-slate-900 text-sm">Live preview</h2>
          </div>
          <div className="p-6" style={{ background: '#F8FAFC' }}>
            {/* Result sheet header mock */}
            <div className="bg-white rounded-xl overflow-hidden shadow">
              <div className="p-4 text-white" style={{ background: primary }}>
                <div className="text-xl font-extrabold">{name || 'Your School Name'}</div>
                <div className="text-xs opacity-90">{motto || 'Your school motto'}</div>
              </div>
              <div className="p-4">
                <div className="font-bold text-sm mb-3">Term result — JSS 2 Gold</div>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span>Mathematics</span><span className="font-bold">82 A1</span></div>
                  <div className="flex justify-between"><span>English</span><span className="font-bold">71 B2</span></div>
                  <div className="flex justify-between"><span>Basic Science</span><span className="font-bold">78 B2</span></div>
                </div>
                <div className="mt-4 pt-3 border-t border-slate-100 flex justify-between items-center">
                  <span className="text-xs text-slate-500">Average: <b>77.0</b></span>
                  <span className="text-xs px-3 py-1 rounded-full text-white font-bold" style={{ background: accent }}>4th of 32</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-4 text-center">This is how your school appears to parents</p>
          </div>
        </div>
      </div>
    </div>
  );
}
